import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { fmtPT } from '../lib/ptTime';
import { useTicketApi } from '../lib/ticketApi';

const day = (iso, tz = 'UTC') => (iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }) : null);

const Row = ({ k, v, testId }) => (v ? <div className="flex gap-3 text-sm"><span className="w-32 shrink-0 text-zinc-400">{k}</span><span className="text-white" data-testid={testId}>{v}</span></div> : null);

// Tap an injury badge → exactly when it was reported, what it is, and the expected return.
export default function InjuryDetails({ ticketId, leg, open, onClose }) {
  const api = useTicketApi();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return;
    setD(null);
    axios.get(`${api.base}/parlays/${ticketId}/legs/${leg.id}/injury`, { headers: api.headers }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [open, ticketId, leg.id, api]);
  const gi = leg.game_injury?.stage ? leg.game_injury : null;
  const f = d?.flag || leg.availability || (gi ? { text: gi.text, reported_at: gi.reported_at, source: gi.source, first_seen_at: gi.at, first_seen_clock: gi.clock } : {});
  const rep = d?.reports?.[0];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-zinc-950 border-zinc-800 text-white" data-testid={`injury-details-${leg.id}`}>
        <DialogHeader><DialogTitle className="pr-8">{leg.player_name} — {f.text || 'Injury'}</DialogTitle></DialogHeader>
        {!d ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div> : (
          <div className="space-y-2">
            {gi && (
              <p className={`rounded px-2 py-1 text-sm font-black ${gi.stage === 'out' ? 'bg-red-600 text-white' : 'bg-orange-500 text-black'}`} data-testid="injury-game-stage">
                {gi.stage === 'out' ? 'OUT FOR THE GAME' : 'HURT — may not return'}{gi.clock ? ` · ${gi.clock}` : ''} · {gi.source}
              </p>
            )}
            <Row k="Status" v={rep?.status || f.text} testId="injury-status" />
            <Row k="Injury" v={rep?.injury} testId="injury-type" />
            <Row k="Reported" v={(rep?.reported_at || f.reported_at) && `${fmtPT(rep?.reported_at || f.reported_at)} (${day(rep?.reported_at || f.reported_at, 'America/Los_Angeles')})`} testId="injury-reported" />
            <Row k="App first saw it" v={f.first_seen_clock === 'before tip-off' ? 'Ruled out before tip-off' : f.first_seen_at && `${fmtPT(f.first_seen_at)}${f.first_seen_clock ? ` · game clock ${f.first_seen_clock}` : ''}`} testId="injury-flagged" />
            <Row k="Last stat" v={d.last_stat_quarter ? `Q${d.last_stat_quarter} (${d.current_value ?? 0} so far)` : null} testId="injury-last-stat" />
            <Row k="Expected back" v={(rep?.return_date || f.return_date) ? `${day(rep?.return_date || f.return_date)} (ESPN estimate)` : 'No return date given'} testId="injury-return" />
            {rep?.short && <p className="text-sm text-zinc-200 border-l-2 border-amber-500 pl-2" data-testid="injury-short">{rep.short}</p>}
            {rep?.long && <p className="text-xs text-zinc-400" data-testid="injury-long">{rep.long}</p>}
            {d.nbc?.length > 0 && (
              <div className="space-y-1.5 pt-1" data-testid="injury-nbc">
                <p className="text-[11px] font-bold uppercase tracking-wider text-orange-300">NBC Sports player news</p>
                {d.nbc.map((n) => (
                  <a key={n.url} href={n.url} target="_blank" rel="noreferrer" className="block rounded border border-zinc-800 bg-zinc-900 p-2 hover:border-orange-400/60" data-testid="injury-nbc-item">
                    <span className="block text-[10px] text-zinc-500">{fmtPT(n.posted_at)} ({day(n.posted_at, 'America/Los_Angeles')}) · {n.status.toUpperCase()}</span>
                    <span className="block text-sm text-zinc-100">{n.headline}</span>
                    {n.analysis && <span className="block text-xs text-zinc-400 mt-0.5">{n.analysis}</span>}
                  </a>
                ))}
              </div>
            )}
            <p className="text-[11px] text-zinc-500 pt-1" data-testid="injury-source">
              Source: {rep ? 'ESPN injury report' : f.source || 'App estimate'}{!rep && f.level === 'possibly_out' ? ': no stats for 2+ quarters and no ESPN report yet, so this is NOT confirmed' : ''}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
